import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Plus, Sparkles, Table, Trash2, UsersRound } from 'lucide-react';

import type { CourseProfile } from '../../types/courseProfile';
import type { RubricCriterion, RubricDraft, RubricLevel } from '../../types/rubricDraft';
import {
  RUBRIC_ROLE_ALL,
  filterCriteriaForRoles,
  isGroupRubric,
  normalizeApplicableRoleIds,
  resolveRubricOas,
} from '../../types/rubricDraft';
import {
  applyAiCriterionToDraft,
  createEmptyCriterion,
} from '../../constants/rubricLevelPresets';
import {
  expandRubricCriterionWithAi,
  mapGenerateRubricError,
} from '../../api/rubricApi';
import { RubricRolesModal } from './RubricRolesModal';

interface RubricMatrixGridProps {
  draft: RubricDraft;
  course: CourseProfile | null;
  onChange: (draft: RubricDraft) => void;
  onStatus?: (message: string | null) => void;
  onError?: (message: string | null) => void;
}

function updateCriterion(
  criteria: RubricCriterion[],
  criterionId: string,
  updater: (criterion: RubricCriterion) => RubricCriterion,
): RubricCriterion[] {
  return criteria.map((criterion) => (criterion.id === criterionId ? updater(criterion) : criterion));
}

/** Intercambia dos criterios por id conservando el resto del orden. */
function swapCriteriaById(
  criteria: RubricCriterion[],
  idA: string,
  idB: string,
): RubricCriterion[] {
  const i = criteria.findIndex((c) => c.id === idA);
  const j = criteria.findIndex((c) => c.id === idB);
  if (i < 0 || j < 0 || i === j) return criteria;
  const next = [...criteria];
  const tmp = next[i];
  next[i] = next[j]!;
  next[j] = tmp!;
  return next;
}

function CriterionRowActions({
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1">
      <button
        type="button"
        className="sala-btn-outline inline-flex h-7 w-7 items-center justify-center p-0 disabled:opacity-35"
        onClick={onMoveUp}
        disabled={!canMoveUp}
        aria-label="Subir criterio"
        title="Subir"
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        className="sala-btn-outline inline-flex h-7 w-7 items-center justify-center p-0 disabled:opacity-35"
        onClick={onMoveDown}
        disabled={!canMoveDown}
        aria-label="Bajar criterio"
        title="Bajar"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        className="sala-btn-outline inline-flex h-7 items-center gap-1 px-2 py-0 text-[10px] text-red-400"
        onClick={onRemove}
        aria-label="Eliminar criterio"
        title="Eliminar criterio"
      >
        <Trash2 className="h-3.5 w-3.5" />
        Eliminar
      </button>
    </div>
  );
}

function CriterionRoleChips({
  draft,
  criterion,
  onToggle,
}: {
  draft: RubricDraft;
  criterion: RubricCriterion;
  onToggle: (roleId: string) => void;
}) {
  const roles = draft.projectRoles ?? [];
  if (!roles.length) return null;

  const selected = normalizeApplicableRoleIds(criterion.applicableRoleIds);
  const isAll = selected.includes(RUBRIC_ROLE_ALL);

  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
      <button
        type="button"
        onClick={() => onToggle(RUBRIC_ROLE_ALL)}
        className="text-[10px] font-medium tracking-wide transition hover:opacity-80"
        style={{
          color: isAll ? 'var(--sala-accent-neon)' : 'var(--sala-ink-dim)',
        }}
      >
        {isAll ? '· Todos los roles' : 'Todos los roles'}
      </button>
      {roles.map((role) => {
        const active = !isAll && selected.includes(role.id);
        return (
          <button
            key={role.id}
            type="button"
            onClick={() => onToggle(role.id)}
            className="inline-flex items-center gap-1.5 text-[10px] transition hover:opacity-80"
            style={{
              color: active ? 'var(--sala-ink)' : 'var(--sala-ink-muted)',
              fontWeight: active ? 600 : 500,
            }}
            title={role.description}
          >
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{
                background: active ? 'var(--sala-accent)' : 'var(--sala-ink-dim)',
                boxShadow: active ? '0 0 0 1px color-mix(in srgb, var(--sala-accent) 40%, transparent)' : undefined,
              }}
              aria-hidden
            />
            {role.name}
          </button>
        );
      })}
    </div>
  );
}

/** Textarea que crece con el contenido (sin scroll interno silencioso). */
function AutoGrowTextarea({
  value,
  onChange,
  className,
  placeholder,
  minRows = 3,
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  minRows?: number;
}) {
  const ref = React.useRef<HTMLTextAreaElement>(null);

  const syncHeight = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, minRows * 20)}px`;
  }, [minRows]);

  React.useLayoutEffect(() => {
    syncHeight();
  }, [value, syncHeight]);

  return (
    <textarea
      ref={ref}
      className={className}
      value={value}
      rows={minRows}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      onInput={syncHeight}
    />
  );
}

function dominantRoleKey(criterion: RubricCriterion): string {
  const ids = normalizeApplicableRoleIds(criterion.applicableRoleIds);
  if (ids.includes(RUBRIC_ROLE_ALL) || !ids.length) return RUBRIC_ROLE_ALL;
  return ids[0];
}

type CriterionGroup = { key: string; label: string | null; criteria: RubricCriterion[] };

function groupCriteriaByDominantRole(
  criteria: RubricCriterion[],
  roles: RubricDraft['projectRoles'],
  roleFilter: string,
): CriterionGroup[] {
  if (roleFilter !== 'all' || !roles?.length) {
    return [{ key: 'flat', label: null, criteria }];
  }

  const byKey = new Map<string, RubricCriterion[]>();
  for (const criterion of criteria) {
    const key = dominantRoleKey(criterion);
    const list = byKey.get(key) ?? [];
    list.push(criterion);
    byKey.set(key, list);
  }

  const groups: CriterionGroup[] = [];
  const transversal = byKey.get(RUBRIC_ROLE_ALL);
  if (transversal?.length) {
    groups.push({ key: RUBRIC_ROLE_ALL, label: 'Todos los roles', criteria: transversal });
  }
  for (const role of roles) {
    const list = byKey.get(role.id);
    if (list?.length) {
      groups.push({ key: role.id, label: role.name, criteria: list });
    }
  }
  // Roles huérfanos (ids que ya no existen en projectRoles)
  for (const [key, list] of byKey) {
    if (key === RUBRIC_ROLE_ALL) continue;
    if (roles.some((role) => role.id === key)) continue;
    groups.push({ key, label: 'Otros', criteria: list });
  }
  return groups.length ? groups : [{ key: 'flat', label: null, criteria }];
}

function CriterionAiExpand({
  draft,
  course,
  criterion,
  onChange,
  onStatus,
  onError,
}: {
  draft: RubricDraft;
  course: CourseProfile | null;
  criterion: RubricCriterion;
  onChange: (draft: RubricDraft) => void;
  onStatus?: (message: string | null) => void;
  onError?: (message: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);

  const handleExpand = async () => {
    if (!course) {
      onError?.('Necesitas un curso activo para usar la IA.');
      return;
    }
    // La instrucción del panel manda. Si está vacía, se usa nombre/descripción
    // del criterio como única semilla (no se reenvían como "borrador" aparte).
    const typedInstruction = instruction.trim();
    const seed =
      typedInstruction ||
      criterion.description.trim() ||
      criterion.name.trim();
    if (seed.length < 4) {
      onError?.(
        typedInstruction
          ? 'Escribe una instrucción más clara (mínimo 4 caracteres).'
          : 'Escribe la instrucción del nuevo criterio, o un nombre/descripción en la fila (mínimo 4 caracteres).',
      );
      return;
    }

    setBusy(true);
    onError?.(null);
    try {
      const selectedOas = resolveRubricOas(draft);
      const { criterion: aiCriterion, provider } = await expandRubricCriterionWithAi({
        instruction: seed,
        // No reenviar el criterio viejo: evita que la IA conserve vestigios.
        // La semilla ya es `instruction` (texto del panel o idea de la fila).
        criterionName: '',
        criterionDescription: '',
        title: draft.title,
        objective: draft.objective.trim() || seed,
        evaluationType: draft.evaluationType ?? 'formativa',
        levels: draft.levels,
        scope: draft.scope ?? 'individual',
        projectRoles: draft.projectRoles,
        courseProfile: course,
        curriculumTarget: draft.curriculumTarget ?? null,
        oaText: selectedOas.map((oa) => oa.text).join('\n'),
        oas: selectedOas,
      });
      onChange(applyAiCriterionToDraft(draft, criterion.id, aiCriterion));
      onStatus?.(
        provider === 'scaffold'
          ? 'Criterio generado en modo local (Mistral no configurado).'
          : 'Criterio generado con IA a partir de tu instrucción.',
      );
      setOpen(false);
      setInstruction('');
    } catch (e) {
      const code = e instanceof Error ? e.message : 'expand_criterion_failed';
      onError?.(mapGenerateRubricError(code));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2">
      {!open ? (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[10px] font-semibold text-[var(--sala-accent-neon)] hover:underline"
          onClick={() => setOpen(true)}
        >
          <Sparkles className="h-3 w-3" />
          Completar con IA
        </button>
      ) : (
        <div
          className="rounded-lg border p-2"
          style={{ borderColor: 'var(--sala-border-strong)' }}
        >
          <label className="block text-[10px] font-semibold uppercase tracking-wide text-[var(--sala-ink-muted)]">
            Instrucción para la IA
          </label>
          <p className="mt-0.5 text-[10px] leading-snug text-[var(--sala-ink-dim)]">
            Genera el criterio solo con esta intención (reemplaza el de esta fila). No reutiliza el texto anterior.
          </p>
          <textarea
            className="sala-input-dark mt-1 min-h-[56px] w-full resize-y text-xs shadow-sm"
            value={instruction}
            onChange={(event) => setInstruction(event.target.value)}
            placeholder="Ej: Calidad de la locución y claridad articulatoria…"
            disabled={busy}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              className="sala-btn-hero py-1 px-2.5 text-[10px]"
              onClick={() => void handleExpand()}
              disabled={busy || !course}
            >
              {busy ? (
                <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="mr-1 inline h-3 w-3" />
              )}
              {busy ? 'Generando…' : 'Generar criterio'}
            </button>
            <button
              type="button"
              className="sala-btn-outline py-1 px-2.5 text-[10px]"
              onClick={() => {
                setOpen(false);
                setInstruction('');
              }}
              disabled={busy}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function RubricMatrixGrid({
  draft,
  course,
  onChange,
  onStatus,
  onError,
}: RubricMatrixGridProps) {
  const [rolesOpen, setRolesOpen] = useState(false);
  const [roleFilter, setRoleFilter] = useState<'all' | string>('all');
  const showEmpty = !draft.hasMatrix || draft.criteria.length === 0;
  const isGroup = isGroupRubric(draft);
  const projectRoles = isGroup ? draft.projectRoles ?? [] : [];
  const hasRoles = projectRoles.length > 0;

  const visibleCriteria = useMemo(() => {
    if (!hasRoles || roleFilter === 'all') return draft.criteria;
    return filterCriteriaForRoles(draft.criteria, [roleFilter]);
  }, [draft.criteria, hasRoles, roleFilter]);

  const criterionGroups = useMemo(
    () => groupCriteriaByDominantRole(visibleCriteria, projectRoles, roleFilter),
    [visibleCriteria, projectRoles, roleFilter],
  );

  const patchCriteria = (criteria: RubricCriterion[], hasMatrix = true) => {
    onChange({ ...draft, criteria, hasMatrix });
  };

  const handleAddCriterion = () => {
    const nextIndex = draft.criteria.length;
    patchCriteria([...draft.criteria, createEmptyCriterion(nextIndex, draft.levels)]);
  };

  const handleRemoveCriterion = (criterionId: string) => {
    const criteria = draft.criteria.filter((criterion) => criterion.id !== criterionId);
    onChange({
      ...draft,
      criteria,
      hasMatrix: criteria.length > 0,
    });
    onStatus?.(criteria.length > 0 ? 'Criterio eliminado.' : 'Matriz vacía: puedes generar de nuevo o añadir criterios.');
  };

  /** Reordena respecto a los hermanos visibles (grupo o lista filtrada). */
  const handleMoveCriterion = (
    criterionId: string,
    direction: 'up' | 'down',
    siblings: RubricCriterion[],
  ) => {
    const idx = siblings.findIndex((criterion) => criterion.id === criterionId);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= siblings.length) return;
    const neighbor = siblings[targetIdx];
    if (!neighbor) return;
    patchCriteria(swapCriteriaById(draft.criteria, criterionId, neighbor.id));
  };

  const handleAddLevel = () => {
    const nextIndex = draft.levels.length + 1;
    const newLevel: RubricLevel = {
      id: `nivel-${nextIndex}`,
      name: `Nivel ${nextIndex}`,
      points: Math.max(1, 6 - nextIndex),
    };
    const levels = [...draft.levels, newLevel];
    const levelCount = Math.min(5, Math.max(3, levels.length)) as RubricDraft['levelCount'];
    const criteria = draft.criteria.map((criterion) => ({
      ...criterion,
      cells: { ...criterion.cells, [newLevel.id]: '' },
    }));
    onChange({
      ...draft,
      levels,
      levelCount,
      criteria: criteria.length > 0 ? criteria : [createEmptyCriterion(0, levels)],
      hasMatrix: true,
    });
  };

  const toggleCriterionRole = (criterionId: string, roleId: string) => {
    patchCriteria(
      updateCriterion(draft.criteria, criterionId, (row) => {
        const current = normalizeApplicableRoleIds(row.applicableRoleIds);
        if (roleId === RUBRIC_ROLE_ALL) {
          return { ...row, applicableRoleIds: [RUBRIC_ROLE_ALL] };
        }
        const withoutAll = current.filter((id) => id !== RUBRIC_ROLE_ALL);
        const next = withoutAll.includes(roleId)
          ? withoutAll.filter((id) => id !== roleId)
          : [...withoutAll, roleId];
        return {
          ...row,
          applicableRoleIds: next.length ? next : [RUBRIC_ROLE_ALL],
        };
      }),
    );
  };

  const toolbar = (
    <div className="flex flex-wrap gap-2">
      {isGroup ? (
        <button
          type="button"
          className="sala-btn-outline py-1.5 text-[11px] shadow-sm"
          onClick={() => setRolesOpen(true)}
        >
          <UsersRound className="mr-1 inline h-3.5 w-3.5" />
          Gestionar roles del proyecto
        </button>
      ) : null}
      {isGroup && hasRoles ? (
        <label className="inline-flex items-center gap-1.5 text-[11px] text-[var(--sala-ink-muted)]">
          Ver por rol
          <select
            className="sala-input-dark py-1 text-[11px]"
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            style={
              roleFilter !== 'all'
                ? {
                    borderColor: 'var(--sala-accent)',
                    boxShadow: '0 0 0 1px color-mix(in srgb, var(--sala-accent) 35%, transparent)',
                  }
                : undefined
            }
          >
            <option value="all">Todos (vista maestra)</option>
            {projectRoles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <button type="button" className="sala-btn-outline py-1.5 text-[11px] shadow-sm" onClick={handleAddCriterion}>
        <Plus className="mr-1 inline h-3.5 w-3.5" />
        Añadir criterio
      </button>
      {draft.levels.length < 5 ? (
        <button type="button" className="sala-btn-outline py-1.5 text-[11px] shadow-sm" onClick={handleAddLevel}>
          <Plus className="mr-1 inline h-3.5 w-3.5" />
          Añadir nivel
        </button>
      ) : null}
    </div>
  );

  return (
    <>
      {isGroup ? (
        <RubricRolesModal
          open={rolesOpen}
          draft={draft}
          onClose={() => setRolesOpen(false)}
          onChange={onChange}
        />
      ) : null}

      {showEmpty ? (
        <div
          className="flex h-full min-h-[480px] flex-col items-center justify-center rounded-2xl border border-dashed p-10 text-center shadow-sm"
          style={{
            borderColor: 'var(--sala-border-strong)',
            background: 'var(--sala-surface-raised)',
          }}
        >
          <div
            className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl shadow-sm"
            style={{
              background: 'color-mix(in srgb, var(--sala-accent) 12%, transparent)',
              border: '1px solid var(--sala-border-strong)',
            }}
          >
            <Table className="h-8 w-8 text-[var(--sala-accent-neon)]" strokeWidth={1.75} />
          </div>
          <h3 className="font-display text-xl font-bold">Matriz de evaluación</h3>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-[var(--sala-ink-muted)]">
            Escribe el objetivo de aprendizaje en el panel lateral y presiona{' '}
            <span className="inline-flex items-center gap-1 font-semibold text-[var(--sala-ink)]">
              <Sparkles className="inline h-3.5 w-3.5" />
              Generar Matriz con IA
            </span>{' '}
            para obtener criterios y descriptores. También puedes armar la tabla manualmente y completar cada criterio con IA.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">{toolbar}</div>
        </div>
      ) : (
        <div
          className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border shadow-sm"
          style={{
            borderColor: 'var(--sala-border-strong)',
            background: 'var(--sala-surface-raised)',
          }}
        >
          <div
            className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3"
            style={{ borderColor: 'var(--sala-border-strong)' }}
          >
            <div className="flex items-center gap-2">
              <Table className="h-4 w-4 text-[var(--sala-accent-neon)]" strokeWidth={2.25} />
              <h3 className="font-display text-sm font-bold uppercase tracking-wider">Matriz de evaluación</h3>
            </div>
            {toolbar}
          </div>

          {isGroup && !hasRoles ? (
            <p
              className="border-b px-4 py-2 text-[11px] text-[var(--sala-ink-muted)]"
              style={{ borderColor: 'var(--sala-border-strong)' }}
            >
              Define roles del proyecto para que la IA asigne criterios por participación. Mientras no haya roles, los
              criterios se tratan como transversales.
            </p>
          ) : null}

          <div className="min-h-0 flex-1 overflow-auto">
            <table className="rubric-matrix w-full min-w-[720px] border-collapse text-left">
              <thead>
                <tr>
                  <th
                    className="sticky left-0 top-0 z-20 min-w-[220px] border-b border-r px-4 py-3 text-xs font-bold uppercase tracking-wide shadow-sm"
                    style={{
                      borderColor: 'var(--sala-border-strong)',
                      background: 'var(--sala-surface)',
                      color: 'var(--sala-ink-muted)',
                    }}
                  >
                    Criterio / Dimensión
                  </th>
                  {draft.levels.map((level) => (
                    <th
                      key={level.id}
                      className="sticky top-0 z-10 min-w-[180px] border-b px-3 py-3 text-xs font-bold uppercase tracking-wide shadow-sm"
                      style={{
                        borderColor: 'var(--sala-border-strong)',
                        background: 'color-mix(in srgb, var(--sala-accent) 14%, var(--sala-surface))',
                        color: 'var(--sala-ink)',
                      }}
                    >
                      <span className="block">{level.name}</span>
                      {level.points != null ? (
                        <span className="mt-0.5 block text-[10px] font-normal normal-case text-[var(--sala-ink-muted)]">
                          {level.points} pts
                        </span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleCriteria.length === 0 ? (
                  <tr>
                    <td
                      colSpan={draft.levels.length + 1}
                      className="px-4 py-8 text-center text-sm text-[var(--sala-ink-muted)]"
                    >
                      No hay criterios para este rol. Etiqueta criterios o cambia el filtro.
                    </td>
                  </tr>
                ) : (
                  criterionGroups.flatMap((group) => {
                    const headerRow =
                      group.label != null ? (
                        <tr key={`group-${group.key}`}>
                          <td
                            colSpan={draft.levels.length + 1}
                            className="border-b px-4 pb-1.5 pt-4"
                            style={{ borderColor: 'var(--sala-border-strong)' }}
                          >
                            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--sala-ink-dim)]">
                              {group.label}
                            </p>
                            <div
                              className="mt-1.5 h-px w-full"
                              style={{ background: 'var(--sala-border-strong)' }}
                            />
                          </td>
                        </tr>
                      ) : null;

                    const criterionRows = group.criteria.map((criterion, criterionIndex) => (
                      <tr key={criterion.id} className="rubric-matrix__row align-stretch">
                        <td
                          className="sticky left-0 z-10 border-b border-r p-3 shadow-sm"
                          style={{
                            borderColor: 'var(--sala-border-strong)',
                            background: 'var(--sala-surface)',
                          }}
                        >
                          <input
                            className="sala-input-dark rubric-matrix__title mb-1.5 w-full shadow-sm"
                            value={criterion.name}
                            onChange={(event) =>
                              patchCriteria(
                                updateCriterion(draft.criteria, criterion.id, (row) => ({
                                  ...row,
                                  name: event.target.value,
                                })),
                              )
                            }
                            placeholder="Nombre del criterio"
                          />
                          {criterion.oaIds && criterion.oaIds.length > 0 ? (
                            <div className="mb-1.5 flex flex-wrap gap-1">
                              {criterion.oaIds.map((oaId) => (
                                <span
                                  key={`${criterion.id}-${oaId}`}
                                  className="rounded border px-1.5 py-0.5 text-[10px] font-medium tracking-wide"
                                  style={{
                                    borderColor: 'var(--sala-border-strong)',
                                    color: 'var(--sala-ink-dim)',
                                  }}
                                >
                                  {oaId}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          <AutoGrowTextarea
                            className="sala-input-dark rubric-matrix__desc w-full shadow-sm"
                            value={criterion.description}
                            minRows={2}
                            onChange={(next) =>
                              patchCriteria(
                                updateCriterion(draft.criteria, criterion.id, (row) => ({
                                  ...row,
                                  description: next,
                                })),
                              )
                            }
                            placeholder="Qué observas / idea del criterio"
                          />
                          {isGroup ? (
                            <CriterionRoleChips
                              draft={draft}
                              criterion={criterion}
                              onToggle={(roleId) => toggleCriterionRole(criterion.id, roleId)}
                            />
                          ) : null}
                          <CriterionRowActions
                            canMoveUp={criterionIndex > 0}
                            canMoveDown={criterionIndex < group.criteria.length - 1}
                            onMoveUp={() => handleMoveCriterion(criterion.id, 'up', group.criteria)}
                            onMoveDown={() => handleMoveCriterion(criterion.id, 'down', group.criteria)}
                            onRemove={() => handleRemoveCriterion(criterion.id)}
                          />
                          <CriterionAiExpand
                            draft={draft}
                            course={course}
                            criterion={criterion}
                            onChange={onChange}
                            onStatus={onStatus}
                            onError={onError}
                          />
                        </td>
                        {draft.levels.map((level) => (
                          <td
                            key={`${criterion.id}-${level.id}`}
                            className="border-b p-2"
                            style={{ borderColor: 'var(--sala-border-strong)' }}
                          >
                            <AutoGrowTextarea
                              className="sala-input-dark rubric-matrix__cell w-full shadow-sm"
                              value={criterion.cells[level.id] ?? ''}
                              minRows={4}
                              onChange={(next) =>
                                patchCriteria(
                                  updateCriterion(draft.criteria, criterion.id, (row) => ({
                                    ...row,
                                    cells: { ...row.cells, [level.id]: next },
                                  })),
                                )
                              }
                              placeholder={`Descriptor — ${level.name}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ));

                    return headerRow ? [headerRow, ...criterionRows] : criterionRows;
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
